import React, { useMemo } from "react";
import {
  Box,
  ExpandableSection,
  LineChart,
  MixedLineBarChartProps,
} from "@amzn/awsui-components-react";
import { InvestigationItemsHistogram } from "../../lusca-search-api/generated-src";

interface InvestigationItemsChartProps {
  histogram?: InvestigationItemsHistogram;
  /** True while the server is answering; keeps the last chart on screen. */
  loading?: boolean;
  /** True when a text filter is applied, so the caption can say the chart follows it. */
  filtered?: boolean;
}

export const DLQ_SERIES_TITLE = "Items by DLQ time";
export const TRANSACTION_SERIES_TITLE = "Items by transaction date";

/** Parse a `YYYY-MM-DD` day (UTC) into a Date at midnight UTC. */
export function dayToDate(day: string): Date {
  return new Date(`${day}T00:00:00Z`);
}

const formatDay = (d: Date) =>
  d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export function histogramSeries(
  histogram?: InvestigationItemsHistogram
): MixedLineBarChartProps.LineDataSeries<Date>[] {
  const toPoints = (
    buckets?: { day?: string; count?: number }[]
  ): MixedLineBarChartProps.Datum<Date>[] =>
    (buckets ?? [])
      .filter((b): b is { day: string; count: number } => !!b.day)
      .map((b) => ({ x: dayToDate(b.day), y: b.count ?? 0 }));
  const series: MixedLineBarChartProps.LineDataSeries<Date>[] = [];
  const dlq = toPoints(histogram?.dlqByDay);
  const transaction = toPoints(histogram?.transactionByDay);
  if (dlq.length) {
    series.push({ title: DLQ_SERIES_TITLE, type: "line", data: dlq });
  }
  if (transaction.length) {
    series.push({
      title: TRANSACTION_SERIES_TITLE,
      type: "line",
      data: transaction,
    });
  }
  return series;
}

/**
 * Daily distribution of the frozen population: when the transactions happened vs when they hit
 * the DLQ. A wide gap between the two lines means old transactions are only now failing (or being
 * replayed); overlapping lines mean fresh traffic is failing on arrival.
 *
 * Counts come from the server over every manifest row matching the current filter, not just the
 * visible page.
 */
export const InvestigationItemsChart: React.FC<
  InvestigationItemsChartProps
> = ({ histogram, loading, filtered }) => {
  const series = useMemo(() => histogramSeries(histogram), [histogram]);
  const missing = histogram?.missingTransactionDate ?? 0;
  const status = loading && !series.length ? "loading" : "finished";

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="Items per day"
      headerDescription={
        filtered
          ? "Counts follow the current filter."
          : "When the transactions happened vs when they reached the DLQ."
      }
    >
      <LineChart<Date>
        series={series}
        xScaleType="time"
        height={180}
        hideFilter
        statusType={status}
        loadingText="Loading distribution"
        ariaLabel="Population items per day by DLQ time and transaction date"
        xTitle="Day (UTC)"
        yTitle="Items"
        xTickFormatter={formatDay}
        i18nStrings={{
          xTickFormatter: formatDay,
          detailPopoverDismissAriaLabel: "Dismiss",
          legendAriaLabel: "Legend",
          chartAriaRoleDescription: "line chart",
        }}
        detailPopoverSeriesContent={({ series: s, y }) => ({
          key: s.title,
          value: y,
        })}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No data</b>
            <Box variant="p" color="inherit">
              No dated items to chart.
            </Box>
          </Box>
        }
      />
      {missing > 0 && (
        <Box
          color="text-body-secondary"
          fontSize="body-s"
          padding={{ top: "xs" }}
        >
          {missing} item{missing === 1 ? "" : "s"} without a transaction date
          (frozen before the field was captured) are only counted by DLQ time.
        </Box>
      )}
    </ExpandableSection>
  );
};

export default InvestigationItemsChart;
